/**
 * 起動と各層の接続（docs/02_architecture.md 5・6 章）。
 * P1：シミュレーションを動かし、作戦フェーズ・レース・予定外の着陸・日の終わりまでを通して遊べる。
 */
import '../ui/styles/main.css';
import * as THREE from 'three';
import { CSS2DRenderer } from 'three/addons/renderers/CSS2DRenderer.js';
import { TIME } from '../config/balance';
import { PLAYER_DEFAULT, RIVALS } from '../config/rivals';
import { DEFAULT_MAP_SEED } from '../config/world';
import { describeEvent } from '../commentary/basic';
import { createRng } from '../core/rng';
import { formatClock, formatDuration } from '../core/units';
import {
  BIOME_LABEL_JA,
  NODE_KIND_LABEL_JA,
  type EdgeData,
  type NodeData,
  type WorldData,
} from '../core/world';
import type { Action } from '../input/keymap';
import { bindKeyboard } from '../input/keyboard';
import { AthleteModels } from '../render/actors/athleteModels';
import { AthletePins, STATE_LABEL_JA } from '../render/actors/athletePins';
import { Trails } from '../render/actors/trails';
import { CameraRig } from '../render/camera/cameraRig';
import { CAMERA_PRESETS } from '../render/camera/followCamera';
import { FollowCamera } from '../render/camera/followCamera';
import { createOverviewCamera } from '../render/camera/overviewCamera';
import { CloudField } from '../render/clouds/cloudField';
import { createCourseMarkers } from '../render/overlays/courseMarkers';
import {
  arcsToPolyline,
  createEdgeLines,
  FatSegments,
  remainingArcPolyline,
} from '../render/overlays/edgeLines';
import { drapedCircle, DynamicLine } from '../render/overlays/groundShapes';
import { NodeMarkers, nodeKindColor, type MarkerState } from '../render/overlays/nodeMarkers';
import { WeatherOverlays } from '../render/overlays/weatherOverlays';
import { createRenderContext } from '../render/renderer';
import { createSkyAndLight } from '../render/sky/skyAndLight';
import {
  createLakes,
  createTerrain,
  drapePolyline,
  overlayLift,
  renderedHeightAt,
} from '../render/terrain/terrainMesh';
import { edgeTimeS } from '../sim/athlete/hike';
import { sinkFactor } from '../sim/athlete/stats';
import { launchOutlook } from '../sim/flight/launch';
import { trimGlideRatio } from '../sim/flight/physics';
import { Sim } from '../sim/sim';
import type { AthleteState } from '../sim/types';
import { thermalRatio } from '../sim/weather/truth';
import { findPath } from '../sim/world/pathfind';
import { createLayout } from '../ui/layout';
import { createLogView } from '../ui/log/logView';
import { createCameraBar } from '../ui/panels/cameraBar';
import { createCompass } from '../ui/panels/compass';
import { createRoutePanel, type RouteLegView, type RoutePanelModel } from '../ui/panels/routePanel';
import { createTooltip } from '../ui/panels/tooltip';
import { createTopBar } from '../ui/panels/topBar';
import { compass8, createWeatherTimeline, type TimelineSegment } from '../ui/panels/weatherTimeline';
import { createDialogs, type ResultRow } from '../ui/screens/dialogs';
import { createLoadingScreen } from '../ui/screens/loading';
import { itemsFromAthlete, Planner } from './planner';
import { loadWorld } from './worldLoader';

const nodeLabel = (n: NodeData): string => n.name ?? NODE_KIND_LABEL_JA[n.kind];
const deg = (rad: number): number => ((((rad * 180) / Math.PI) % 360) + 360) % 360;

async function main(): Promise<void> {
  const params = new URLSearchParams(location.search);
  const mapSeed = Number(params.get('seed') ?? DEFAULT_MAP_SEED);
  // 天気は毎回変わる（?weather= で固定できる）。app 層なので Math.random を使ってよい
  const weatherSeed = Number(params.get('weather') ?? Math.floor(Math.random() * 1e9));

  const layout = createLayout(document.body);
  const loading = createLoadingScreen(document.body);
  const log = createLogView(layout.log);

  let world: WorldData;
  try {
    world = await loadWorld(mapSeed, loading.setProgress);
  } catch (err) {
    loading.showError(err instanceof Error ? err.message : String(err));
    return;
  }

  // ---- sim ----
  const sim = new Sim(world, { weatherSeed, roster: [PLAYER_DEFAULT, ...RIVALS] });
  const player = sim.athletes.find((a) => a.isPlayer) as AthleteState;
  const graph = sim.graph;
  const nameOf = (id: string): string => sim.athlete(id)?.name ?? id;
  const isPlayer = (id: string): boolean => id === player.id;
  const commentaryRng = createRng(weatherSeed, 'commentary');
  const glideRatio = (): number => trimGlideRatio(sinkFactor(player.stats)) * 0.85;
  const planner = new Planner(
    graph,
    () => sim.hikeMods(player),
    glideRatio,
    (x, z) => sim.terrain.heightAt(x, z),
  );

  // ---- 描画 ----
  const ctx = createRenderContext(layout.canvasHost);
  const sky = createSkyAndLight(ctx.scene);
  const height = (x: number, z: number): number => sim.terrain.heightAt(x, z);
  const drape = (pts: readonly number[]): number[] => drapePolyline(world, pts);
  const edgeLines = createEdgeLines(world.edges, drape);
  ctx.scene.add(createTerrain(world), createLakes(world), edgeLines);
  let markers = new NodeMarkers(graph.nodes);
  markers.setPixelRatio(ctx.renderer.getPixelRatio());
  ctx.scene.add(markers.object);
  const hikeLine = new FatSegments(0xfacc15, 5);
  /** 今歩いている区間の残り（予約のルートは区間の終わりから先なので、その手前を補う） */
  const currentLine = new FatSegments(0xfacc15, 5);
  const flightLine = new FatSegments(0xfacc15, 4, true);
  const reachLine = new DynamicLine(
    new THREE.LineDashedMaterial({
      color: 0xffffff,
      dashSize: 500,
      gapSize: 350,
      depthTest: false,
      transparent: true,
    }),
  );
  ctx.scene.add(hikeLine.object, currentLine.object, flightLine.object, reachLine.line);
  const courseMarkers = createCourseMarkers(world.nodes, world.course.nodeIds);
  ctx.scene.add(courseMarkers.group);
  const shownHeight = (x: number, z: number): number => renderedHeightAt(world, x, z);
  const models = new AthleteModels(sim.athletes, shownHeight, () => graph.nodes);
  ctx.scene.add(models.group);
  /** 追従カメラで追っている選手（ピンの名前のクリック・[ ] キーで切り替え） */
  let watchId = player.id;
  const pins = new AthletePins(sim.athletes, (id) => watch(id));
  const trails = new Trails(sim.athletes);
  const clouds = new CloudField();
  const weatherOverlays = new WeatherOverlays(height, world);
  ctx.scene.add(pins.group, trails.group, clouds.mesh, weatherOverlays.group);

  // 俯瞰の操作は代理のカメラを動かす（追従と 1 秒かけて切り替えるため。cameraRig.ts）
  const mapCam = ctx.camera.clone();
  const labels = new CSS2DRenderer({ element: layout.labelHost });
  const resize = (): void => {
    mapCam.aspect = ctx.camera.aspect;
    mapCam.updateProjectionMatrix();
    labels.setSize(ctx.size.width, ctx.size.height);
    hikeLine.setResolution(ctx.size.width, ctx.size.height);
    currentLine.setResolution(ctx.size.width, ctx.size.height);
    flightLine.setResolution(ctx.size.width, ctx.size.height);
  };
  new ResizeObserver(resize).observe(layout.view);
  resize();

  const cam = createOverviewCamera(mapCam, ctx.renderer.domElement, world, height);
  const follow = new FollowCamera(shownHeight);
  const rig = new CameraRig(ctx.camera, cam, mapCam, follow);
  /** 地図で選手を見るときの中心（右のパネルに隠れないよう少しずらす） */
  const playerFocus = (): { x: number; z: number } => ({ x: player.pos.x + 12_000, z: player.pos.z - 3_000 });
  /** レース中の見え方の好み（M キーで切り替え）。作戦・編集・ダイアログの間は地図になる */
  let viewPref: 'follow' | 'map' = 'follow';
  /** 追う選手を変える（角度はその選手の後ろから始め直す） */
  const watch = (id: string): void => {
    if (id === watchId) return;
    watchId = id;
    follow.reset();
    viewPref = 'follow';
  };
  /** 順位の順に、前・次の選手へ */
  const watchStep = (d: 1 | -1): void => {
    const order = sim.race.standings.length
      ? sim.race.standings.map((st) => st.athleteId)
      : sim.athletes.map((a) => a.id);
    const i = order.indexOf(watchId);
    watch(order[(i + d + order.length) % order.length] as string);
  };
  const focusPlayer = (): void => {
    if (rig.mode === 'follow') {
      watch(player.id);
      follow.yaw = 0;
      follow.pitch = 0;
      follow.zoom = 1;
    } else {
      const f = playerFocus();
      cam.focus(f.x, f.z);
    }
  };
  focusPlayer();

  // ---- UI ----
  let forecastLayerOn = false;
  const dialogs = createDialogs(layout.root);
  const topBar = createTopBar(layout.topBar, {
    setFastForward: (lv) => sim.command({ type: 'setFastForward', level: lv }),
    openForecast: () => dialogs.showForecast(sim.forecast, () => undefined),
    toggleView: () => onAction('toggleView'),
  });
  const timeline = createWeatherTimeline(layout.weather);
  const compass = createCompass(layout.hud);
  const cameraBar = createCameraBar(layout.view, {
    setPreset: (pr) => {
      follow.preset = pr;
    },
    prevAthlete: () => watchStep(-1),
    nextAthlete: () => watchStep(1),
    watchSelf: () => focusPlayer(),
  });
  const tooltip = createTooltip(layout.view);

  const syncPlanner = (): void => {
    const anchor = sim.anchor(player);
    if (anchor.nodeId !== null) {
      planner.origin = anchor.nodeId;
      planner.originEtaS = anchor.etaS;
    }
    if (!planner.editing)
      planner.syncFromSim(itemsFromAthlete(player, anchor.nodeId), anchor.nodeId, anchor.etaS);
  };

  const confirmPlan = (): void => {
    const steps = planner.confirm();
    if (!steps) return;
    sim.command({ type: 'setPlan', athleteId: player.id, steps });
    if (sim.race.pause) sim.command({ type: 'resume' });
    log.add('system', steps.length ? `ルートを確定しました（${steps.length} 区間）` : '予約をクリアしました');
  };

  const inPlanning = (): boolean => sim.race.phase === 'planning';
  const canEdit = (): boolean => sim.race.phase !== 'dayEnd' && sim.race.phase !== 'finished';

  const startDay = (): void => {
    if (!inPlanning() || planner.preview().error) return;
    confirmPlan();
    sim.command({ type: 'start' });
    forecastLayerOn = false;
  };

  const onAction = (a: Action): void => {
    if (dialogs.open) return;
    switch (a) {
      case 'editRoute':
        if (canEdit()) planner.beginEdit();
        break;
      case 'confirm':
        if (inPlanning()) startDay();
        else if (planner.editing) confirmPlan();
        break;
      case 'cancel':
        if (inPlanning()) planner.awaitingFlightTarget = false;
        else planner.cancel();
        break;
      case 'undo':
        planner.undo();
        break;
      case 'clearRoute':
        planner.clear();
        break;
      case 'fly':
        if (canEdit()) planner.startFlight();
        break;
      case 'toggleStyle':
        sim.command({
          type: 'setAutoStyle',
          athleteId: player.id,
          style: player.autoStyle === 'attack' ? 'defend' : 'attack',
        });
        break;
      case 'toggleForecastLayer':
        forecastLayerOn = !forecastLayerOn;
        break;
      case 'toggleView':
        viewPref = viewPref === 'follow' ? 'map' : 'follow';
        break;
      case 'cameraPreset':
        follow.preset =
          CAMERA_PRESETS[(CAMERA_PRESETS.indexOf(follow.preset) + 1) % CAMERA_PRESETS.length] ?? 'mid';
        break;
      case 'watchPrev':
        watchStep(-1);
        break;
      case 'watchNext':
        watchStep(1);
        break;
      case 'focusPlayer':
        focusPlayer();
        break;
      case 'fastForward1':
        sim.command({ type: 'setFastForward', level: 1 });
        break;
      case 'fastForward2':
        sim.command({ type: 'setFastForward', level: 2 });
        break;
      case 'fastForward4':
        sim.command({ type: 'setFastForward', level: 4 });
        break;
    }
  };
  const panel = createRoutePanel(layout.panel, {
    edit: () => onAction('editRoute'),
    confirm: () => onAction('confirm'),
    cancel: () => onAction('cancel'),
    undo: () => onAction('undo'),
    clear: () => onAction('clearRoute'),
    fly: () => onAction('fly'),
    start: () => startDay(),
    toggleStyle: () => onAction('toggleStyle'),
  });
  bindKeyboard(window, onAction);

  const showTooltip = (id: number, x: number, y: number): void => {
    const n = graph.nodes[id] as NodeData;
    const lines = [
      ...(n.name ? [NODE_KIND_LABEL_JA[n.kind]] : []),
      `標高 ${Math.round(n.pos.y).toLocaleString()}m ・ ${BIOME_LABEL_JA[sim.terrain.biomeAt(n.pos.x, n.pos.z)]}`,
    ];
    if (n.launch) {
      const dirs = n.launch.facings.length ? n.launch.facings : [n.launch.facingRad];
      lines.push(`離陸できる向き：${[...new Set(dirs.map((f) => compass8(deg(f))))].join('・')}`);
    }
    const from = planner.endNode();
    if (planner.awaitingFlightTarget) {
      const f = graph.nodes[from] as NodeData;
      const d = Math.hypot(n.pos.x - f.pos.x, n.pos.z - f.pos.z);
      const glide = Math.max(0, f.pos.y - n.pos.y - 150) * glideRatio();
      lines.push(
        `フライト ${(d / 1000).toFixed(1)}km ・ ${d <= glide ? '滑空で届く見込み' : 'サーマルが必要'}`,
      );
    } else if (from !== id) {
      const mods = sim.hikeMods(player);
      const p = findPath(graph, from, id, (e: EdgeData, fwd: boolean) => edgeTimeS(e, fwd, mods));
      lines.push(
        p
          ? `${nodeLabel(graph.nodes[from] as NodeData)}から徒歩 ${formatDuration(p.costS)}`
          : '歩いては行けない',
      );
    }
    tooltip.show({ title: nodeLabel(n), color: nodeKindColor(n.kind), lines }, x, y);
  };

  // クリック（ドラッグと区別する）・右クリック・ホバー
  const canvas = ctx.renderer.domElement;
  let down: { x: number; y: number; button: number } | null = null;
  let hovered: number | null = null;
  const localXY = (e: PointerEvent): { x: number; y: number } => {
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  canvas.addEventListener('pointerdown', (e) => {
    down = { ...localXY(e), button: e.button };
  });
  canvas.addEventListener('pointerup', (e) => {
    const p = localXY(e);
    const d = down;
    down = null;
    if (!d || Math.hypot(p.x - d.x, p.y - d.y) > 5 || !canEdit() || dialogs.open) return;
    if (d.button === 0) {
      const id = markers.pick(ctx.camera, p.x, p.y, ctx.size.width, ctx.size.height);
      if (id !== null) planner.clickNode(id, e.shiftKey ? 'replace' : 'extend');
    } else if (d.button === 2 && (planner.editing || inPlanning())) planner.undo();
  });
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  canvas.addEventListener('pointermove', (e) => {
    if (e.buttons !== 0) {
      tooltip.hide();
      // 追従カメラ：ドラッグで回り込み（左右）と角度（上下）
      if (rig.mode === 'follow') {
        follow.yaw -= e.movementX * 0.005;
        follow.pitch = THREE.MathUtils.clamp(follow.pitch + e.movementY * 0.004, -0.25, 1.2);
      }
      return;
    }
    const p = localXY(e);
    const id = markers.pick(ctx.camera, p.x, p.y, ctx.size.width, ctx.size.height);
    hovered = id;
    canvas.style.cursor = id !== null ? 'pointer' : '';
    if (id !== null) showTooltip(id, p.x, p.y);
    else tooltip.hide();
  });
  canvas.addEventListener('pointerleave', () => tooltip.hide());
  canvas.addEventListener(
    'wheel',
    (e) => {
      if (rig.mode !== 'follow') return;
      e.preventDefault();
      follow.zoom = THREE.MathUtils.clamp(follow.zoom * Math.exp(e.deltaY * 0.0012), 0.15, 8);
    },
    { passive: false },
  );

  // ---- 出来事 ----
  const resultRows = (): ResultRow[] =>
    sim.race.standings.map((s) => {
      const a = sim.athlete(s.athleteId) as AthleteState;
      return {
        rank: s.rank,
        name: a.isPlayer ? 'あなた' : a.name,
        color: a.color,
        isPlayer: a.isPlayer,
        remainingKm: s.remainingM / 1000,
        gapS: s.gapS,
        finished: s.finished,
        todayKm: (a.progressM - a.day.startProgressM) / 1000,
        flights: a.day.flights,
        longestKm: a.day.longestFlightM / 1000,
        stamina: a.stamina,
      };
    });

  const openPlanning = (): void => {
    planner.cancel();
    syncPlanner();
    planner.beginEdit();
    forecastLayerOn = true;
    focusPlayer();
    dialogs.showForecast(sim.forecast, () => undefined);
  };

  /** 予定外の着陸のあと「そのまま再開」したら次に何をするか（押す前に分かるように） */
  const resumeHint = (): string => {
    const next = player.plan[0];
    if (!next || player.nodeId === null)
      return 'そのまま再開すると：予約の続きがないので、ここで止まります。';
    if (next.kind === 'flight')
      return 'そのまま再開すると：ここは離陸地ではないので、予約のフライトは飛ばして次の区間へ進みます。';
    // 未通過の CP より先へ歩く予約なら、sim は先に次の CP へ向かう（sim.ts startNextStep）
    const cpNode = world.course.nodeIds[player.nextCp];
    const skipsCp = world.course.nodeIds.indexOf(next.to) > player.nextCp && cpNode !== undefined;
    const to = skipsCp ? (cpNode as number) : next.to;
    const mods = sim.hikeMods(player);
    const path = findPath(graph, player.nodeId, to, (e: EdgeData, fwd: boolean) => edgeTimeS(e, fwd, mods));
    const label = nodeLabel(graph.nodes[to] as NodeData);
    if (!path) return `そのまま再開すると：${label}へは歩いては行けません。`;
    // 登り・下り（尾根を越え直すような道だと、押す前に分かるように）
    let up = 0;
    let down = 0;
    for (const arc of path.arcs) {
      const e = graph.edges[arc.edgeId] as EdgeData;
      up += arc.forward ? e.ascentM : e.descentM;
      down += arc.forward ? e.descentM : e.ascentM;
    }
    const climbNote = up >= 400 ? '山を登り直す道です。近くの離陸地から飛び直すなら「ルートを組む」へ。' : '';
    return (
      `そのまま再開すると：${skipsCp ? 'まだ通過していない ' : ''}${label}へ徒歩 ${formatDuration(path.costS)}` +
      `（登り ${Math.round(up).toLocaleString()}m・下り ${Math.round(down).toLocaleString()}m）。${climbNote}`
    );
  };

  const handleEvents = (): void => {
    for (const e of sim.drainEvents()) {
      const line = describeEvent(e, nameOf, isPlayer, (n) => commentaryRng.int(0, n - 1));
      if (line) {
        const rival = line.speaker === 'rival' && line.athleteId ? nameOf(line.athleteId) : undefined;
        log.add(line.speaker, line.text, rival);
        if (line.speaker === 'rival' && line.athleteId)
          pins.say(line.athleteId, line.text, performance.now());
      }
      if (e.type === 'tempNodeAdded') {
        ctx.scene.remove(markers.object);
        markers = new NodeMarkers(graph.nodes);
        markers.setPixelRatio(ctx.renderer.getPixelRatio());
        ctx.scene.add(markers.object);
      } else if (e.type === 'forcedPause' && e.athleteId === player.id) {
        watch(player.id);
        const place = `${BIOME_LABEL_JA[sim.terrain.biomeAt(player.pos.x, player.pos.z)]}（標高 ${Math.round(player.pos.y)}m）`;
        cam.focus(player.pos.x, player.pos.z);
        dialogs.showLanding(
          {
            place,
            packMin: Math.max(0, Math.round(player.timerS / 60)),
            stamina: player.stamina,
            resumeHint: resumeHint(),
          },
          () => {
            syncPlanner();
            planner.beginEdit();
          },
          () => sim.command({ type: 'resume' }),
        );
      } else if (e.type === 'dayEnd' && sim.race.phase !== 'finished') {
        dialogs.showResults(`Day${e.dayIndex + 1} リザルト`, resultRows(), '翌日へ', () => {
          sim.command({ type: 'nextDay' });
          sim.advance(0);
          handleEvents();
          openPlanning();
        });
      } else if (e.type === 'raceEnd') {
        dialogs.showResults('最終リザルト', resultRows(), 'もう一度', () => location.reload());
      }
    }
  };

  // ---- 表示の更新 ----
  let scale = 0;
  let last = performance.now();
  let uiAcc = 0;
  let panelKey = '';
  let routeKey = '';
  const camDir = new THREE.Vector3();

  const updateRouteOverlay = (): void => {
    const preview = planner.preview();
    const key = `${planner.editing}:${planner.awaitingFlightTarget}:${JSON.stringify(planner.items)}:${planner.origin}`;
    if (key === routeKey) return;
    routeKey = key;
    const color = planner.editing ? 0x22d3ee : 0xfacc15;
    hikeLine.setColor(color);
    currentLine.setColor(color);
    flightLine.setColor(color);
    const hikes: number[][] = [];
    const flights: number[][] = [];
    for (const leg of preview.legs) {
      if (leg.kind === 'hike') hikes.push(drape(arcsToPolyline(graph.edges, leg.arcs)));
      else {
        const f = graph.nodes[leg.from] as NodeData;
        const t = graph.nodes[leg.to] as NodeData;
        flights.push([f.pos.x, f.pos.y + 150, f.pos.z, t.pos.x, t.pos.y + 150, t.pos.z]);
      }
    }
    hikeLine.setPolylines(hikes);
    flightLine.setPolylines(flights);
    if (planner.awaitingFlightTarget) {
      const n = graph.nodes[planner.endNode()] as NodeData;
      reachLine.set(drapedCircle(n.pos.x, n.pos.z, planner.glideRangeFromEnd(), height, 60, 96));
    } else reachLine.set(null);
  };

  const panelModel = (): RoutePanelModel => {
    const preview = planner.preview();
    const now = sim.race.timeOfDayS;
    const legs: RouteLegView[] = preview.legs.map((leg) => {
      const to = graph.nodes[leg.to] as NodeData;
      const arrive = formatClock(now + leg.startS + leg.durationS);
      return leg.kind === 'hike'
        ? {
            kind: 'hike',
            toLabel: nodeLabel(to),
            toColor: nodeKindColor(to.kind),
            durationS: leg.durationS,
            lengthM: leg.lengthM,
            ascentM: leg.ascentM,
            descentM: leg.descentM,
            arriveClock: arrive,
          }
        : {
            kind: 'flight',
            toLabel: nodeLabel(to),
            toColor: nodeKindColor(to.kind),
            durationS: leg.durationS,
            flightS: leg.flightS,
            lengthM: leg.lengthM,
            reachable: leg.reachable,
            blocked: leg.blocked,
            outlook: launchOutlook(
              sim.forecast,
              graph.nodes[leg.from] as NodeData,
              (now + leg.startS + leg.prepS) / 3600,
              player.stats,
            ).kind,
            arriveClock: arrive,
          };
    });
    const race = sim.race;
    const phase =
      race.phase === 'planning'
        ? 'planning'
        : race.phase === 'running' || race.phase === 'sunsetLanding'
          ? 'running'
          : 'other';
    let statusText = STATE_LABEL_JA[player.mode];
    const here = player.nodeId !== null ? (graph.nodes[player.nodeId] as NodeData) : null;
    if (player.flight) {
      const f = player.flight;
      const agl = f.pos.y - sim.terrain.heightAt(f.pos.x, f.pos.z);
      const toT = Math.hypot(f.target.x - f.pos.x, f.target.z - f.pos.z) / 1000;
      statusText += `：高度 ${Math.round(f.pos.y).toLocaleString()}m（対地 ${Math.round(agl)}m）・上昇 ${f.varioMs.toFixed(1)}m/s・目標まで ${toT.toFixed(1)}km`;
    } else if (player.mode === 'preparing' || player.mode === 'packing')
      statusText += `：残り ${Math.ceil(player.timerS / 60)} 分`;
    else if (player.mode === 'waitingWind') statusText += `：${Math.floor(player.timerS / 60)} 分経過`;
    else if (here) statusText += `：${nodeLabel(here)}（${Math.round(here.pos.y)}m）`;
    if (legs.length) statusText += `\n予約の合計 ${formatDuration(preview.totalS)}`;
    return {
      phase,
      editing: planner.editing && phase !== 'planning',
      awaitingFlightTarget: planner.awaitingFlightTarget,
      canFly: canEdit() && planner.canFlyFromEnd(),
      statusChip:
        phase === 'planning'
          ? '作戦フェーズ（停止中）'
          : planner.editing
            ? '編集中 ×0.25'
            : race.pause
              ? '停止中'
              : STATE_LABEL_JA[player.mode],
      statusText,
      legs,
      error: preview.error,
      autoStyle: player.autoStyle,
    };
  };

  const updateUi = (): void => {
    const race = sim.race;
    const standing = race.standings.find((s) => s.athleteId === player.id);
    const phaseLabel = {
      planning: '作戦フェーズ',
      running: 'レース中',
      sunsetLanding: '日没',
      dayEnd: 'ビバーク',
      finished: '終了',
    }[race.phase];
    const stopped = sim.targetTimeScale() === 0;
    topBar.render({
      dayLabel: `DAY ${race.dayIndex + 1}`,
      phaseLabel,
      clock: formatClock(race.timeOfDayS),
      timeScaleLabel: stopped
        ? race.pause
          ? '停止（予定外の着陸）'
          : '停止'
        : `×${Math.round(scale)}${race.editing ? ' 編集中' : ''}`,
      timeScaleWarn: race.editing || !!race.pause,
      fastForward: race.fastForward,
      canFastForward: race.phase === 'running' || race.phase === 'sunsetLanding',
      stamina: player.stamina,
      view: rig.mode,
      canToggleView: race.phase === 'running' || race.phase === 'sunsetLanding',
      rankLabel: standing
        ? standing.rank === 1
          ? '1位（トップ）'
          : `${standing.rank}位 ${standing.gapS === null ? '' : `+${formatDuration(standing.gapS)}`}`
        : '',
    });

    const f = sim.forecast;
    const now = race.timeOfDayS;
    const segs: TimelineSegment[] = [];
    for (const leg of planner.preview().legs) {
      const a = (now + leg.startS) / 3600;
      if (leg.kind === 'hike') {
        segs.push({
          kind: 'hike',
          fromH: a,
          toH: a + leg.durationS / 3600,
          label: `→${nodeLabel(graph.nodes[leg.to] as NodeData)}`,
        });
      } else {
        const b = a + leg.prepS / 3600;
        const c = b + leg.flightS / 3600;
        segs.push({ kind: 'prep', fromH: a, toH: b, label: '準' });
        segs.push({ kind: 'flight', fromH: b, toH: c, label: '飛行' });
        segs.push({ kind: 'pack', fromH: c, toH: c + leg.packS / 3600, label: '収' });
      }
    }
    timeline.render({
      nowH: now / 3600,
      wind: f.windBlocks.map((b) => ({
        fromH: b.fromH,
        toH: b.toH,
        fromDeg: b.ridge.fromDeg,
        kmh: b.ridge.kmh,
      })),
      thermal: Array.from({ length: 14 }, (_, i) => {
        const h = 7 + i;
        const r = thermalRatio(f.thermal, (h + 0.5) * 3600);
        return { h, strength: r, climbMs: f.thermal.maxClimbMs * Math.sqrt(r) };
      }),
      cloudBase: f.cloudBase,
      storm:
        f.storm.chance > 0
          ? {
              fromH: f.storm.fromH,
              toH: f.storm.toH,
              chance: f.storm.chance,
              region: f.storm.area?.label ?? '',
            }
          : null,
      sunsetH: TIME.sunsetS / 3600,
      plan: segs,
    });

    ctx.camera.getWorldDirection(camDir);
    const gy = sim.terrain.heightAt(player.pos.x, player.pos.z) + 10;
    const wg = sim.weather.windAt(player.pos.x, gy, player.pos.z, now, { x: 0, z: 0 });
    const wr = sim.weather.windAt(player.pos.x, 2500, player.pos.z, now, { x: 0, z: 0 });
    compass.render({
      cameraAzimuth: Math.atan2(camDir.x, -camDir.z),
      ground: { fromDeg: deg(Math.atan2(-wg.x, wg.z)), kmh: Math.hypot(wg.x, wg.z) * 3.6 },
      ridge: { fromDeg: deg(Math.atan2(-wr.x, wr.z)), kmh: Math.hypot(wr.x, wr.z) * 3.6 },
    });

    const model = panelModel();
    const key = JSON.stringify(model);
    if (key !== panelKey) {
      panelKey = key;
      panel.render(model);
    }
  };

  // ---- 開始 ----
  log.add('system', `ワールドを生成しました（seed ${mapSeed}、天気 ${weatherSeed}）`);
  log.add('announcer', 'アルプス横断レース、Day1 の朝です。スタート前に作戦を立てましょう！');
  log.add('commentator', 'ノードをクリックしてルート、離陸地（▲）で F キーを押すとフライトを予約できます。');
  log.add(
    'commentator',
    'CP は地上で通過します。空から上を越えても通過にならないので、CP に降りて次の山へ登りましょう。',
  );
  handleEvents();
  syncPlanner();
  openPlanning();
  loading.hide();

  const frame = (nowMs: number): void => {
    const realDt = Math.min(0.1, Math.max(0, (nowMs - last) / 1000));
    last = nowMs;

    const editing = planner.editing && !inPlanning();
    if (editing !== sim.race.editing) sim.command({ type: 'setEditing', editing });
    const target = sim.targetTimeScale();
    if (target === 0) scale = 0;
    else if (scale === 0) scale = target;
    else scale *= Math.pow(target / scale, 1 - Math.exp(-realDt / 0.5));
    sim.advance(realDt * scale);
    handleEvents();
    syncPlanner();

    const states = new Map<number, MarkerState>();
    states.set(planner.origin, 'route');
    for (const it of planner.items) states.set(it.to, 'route');
    if (hovered !== null) states.set(hovered, 'hover');
    markers.setStates(states);
    updateRouteOverlay();

    // カメラ：作戦・編集・ダイアログ・日の終わりは地図。レース中は好みの見え方（標準は追従）
    const needMap =
      inPlanning() ||
      planner.editing ||
      dialogs.open ||
      sim.race.phase === 'dayEnd' ||
      sim.race.phase === 'finished';
    rig.setMode(needMap || viewPref === 'map' ? 'map' : 'follow', playerFocus());
    models.update(sim.athletes, ctx.camera, nowMs, realDt);
    const watched = sim.athlete(watchId) ?? player;
    const pose = models.pose(watched.id);
    if (pose) {
      const flying = watched.mode === 'flying';
      rig.update(realDt, {
        pos: pose.pos,
        flying,
        // 注視点：歩きは胸、飛行はパイロットとキャノピーの間（モデルの拡大に合わせる）
        lookUp: (flying ? 3.5 : 1.0) * pose.scale,
        // 離陸準備中は離陸の向き（パイロットの後ろから谷を見る）。ほかは進んだ向きからカメラが決める
        headingHint: watched.mode === 'preparing' || watched.mode === 'waitingWind' ? pose.heading : null,
      });
    }
    const following = rig.mode === 'follow';
    markers.object.visible = !following;
    // 経路の線：地面に沿わせた線を、カメラの高さに合わせて少しだけ浮かせる。追従では地形の奥の線は隠す
    const camH = ctx.camera.position.y - shownHeight(ctx.camera.position.x, ctx.camera.position.z);
    const lift = overlayLift(camH);
    edgeLines.position.y = lift;
    hikeLine.object.position.y = lift * 1.5;
    hikeLine.setDepthTest(following);
    currentLine.object.position.y = lift * 1.5;
    currentLine.setDepthTest(following);
    // 今歩いている区間の残りは毎フレーム作り直す（時間が速いので、間引くと線の始まりが選手より後ろに残る）
    {
      const route = player.mode === 'hiking' ? player.route : null;
      const arc = route?.arcs[route.index];
      const edge = arc ? graph.edges[arc.edgeId] : undefined;
      currentLine.setPolylines(
        route && arc && edge ? [drape(remainingArcPolyline(edge, arc.forward, route.distM, player.pos))] : [],
      );
    }
    pins.update(sim.athletes, sim.race.standings, nowMs, (a) =>
      following
        ? // 「遠」では選手が小さいので、追っている選手のピンも出す
          (a.id !== watched.id || follow.preset === 'far') &&
          Math.hypot(a.pos.x - watched.pos.x, a.pos.z - watched.pos.z) < 20_000
        : true,
    );
    cameraBar.render({
      visible: following,
      preset: follow.preset,
      watched: { name: watched.name, color: watched.color, isPlayer: watched.isPlayer },
    });
    trails.update(sim.athletes, sim.race.activeRaceTimeS);
    clouds.update(sim.weather.state.clouds, sim.weather.state.storms, sim.race.timeOfDayS);
    weatherOverlays.update(sim.weather.state.storms, sim.forecast, sim.race.timeOfDayS);
    weatherOverlays.setForecastVisible(forecastLayerOn || inPlanning());

    uiAcc += realDt;
    if (uiAcc > 0.1) {
      uiAcc = 0;
      updateUi();
    }

    courseMarkers.update(ctx.camera);
    sky.update(ctx.camera);
    ctx.renderer.render(ctx.scene, ctx.camera);
    labels.render(ctx.scene, ctx.camera);
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  // デバッグ用（?debug=1）：開発ツールから状態を参照し、ページが非表示でもフレームを手動で進められるようにする
  if (params.get('debug') === '1') {
    Object.assign(window, {
      __game: {
        sim,
        ctx,
        planner,
        rig,
        models,
        weatherOverlays,
        reachLine,
        markers: () => markers,
        frame: (ms: number) => frame(ms),
        /** 今の画面を描いて、開発サーバーの reports/<name>.png に保存する（vite.config.ts） */
        snapshot: async (name = 'snapshot'): Promise<string> => {
          ctx.renderer.render(ctx.scene, ctx.camera);
          const body = ctx.renderer.domElement.toDataURL('image/png');
          const res = await fetch(`/__debug/snapshot?name=${encodeURIComponent(name)}`, {
            method: 'POST',
            body,
          });
          return res.text();
        },
      },
    });
  }
}

void main();
